import React, { useState } from "react";
import {
  Box,
  Grid,
  TextField,
  Checkbox,
  IconButton,
  Typography,
  Autocomplete,
} from "@mui/material";
import AddCircleOutlineIcon from "@mui/icons-material/AddCircleOutline";
import DeleteIcon from "@mui/icons-material/Delete";
import { motion } from "framer-motion";

const SubMenuCard = ({
  subMenus,
  setSubMenus,
  permissionsList,
  setSelectedPermissionSubMenus,
  selectedPermissionSubMenus,
}) => {
  const handleInputChange = (index, field, value) => {
    const updatedSubMenus = [...subMenus];
    updatedSubMenus[index][field] = value;
    setSubMenus(updatedSubMenus);
  };

  const handleAddClick = () => {
    setSubMenus([
      ...subMenus,
      {
        name: "",
        icon: "",
        routes: "",
        permission: null,
        is_active: false,
        is_admin: false,
        is_customer: false,
      },
    ]);
  };

  const handleDeleteClick = (index) => {
    const updatedSubMenus = subMenus.filter((_, i) => i !== index);
    setSubMenus(updatedSubMenus);
  };

  return (
    <motion.div
      initial={{ opacity: 0, scale: 0.95, y: -20 }}
      animate={{ opacity: 1, scale: 1, y: 0 }}
      exit={{ opacity: 0, scale: 0.95, y: -20 }}
      transition={{ duration: 0.3, ease: "easeOut" }}
      style={{
        width: "100%",
        backgroundColor: "#eeeef0",
        padding: "10px",
        borderRadius: "10px",
        boxShadow: "0 4px 10px rgba(0, 0, 0, 0.1)",
      }}
    >
      {subMenus.map((submenu, index) => (
        <motion.div
          key={index} // Ensure each submenu has a unique key
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: 20 }} // Exit with smooth fade and upward animation
          transition={{
            duration: 0.3,
            ease: "easeOut",
            delay: index * 0.1, // Add slight delay for each item
          }}
        >
          <Box
            sx={{
              marginBottom: 2,
              backgroundColor: "#ffffff",
              borderRadius: 2,
              padding: 1,
              boxShadow: "0 2px 5px rgba(0, 0, 0, 0.1)",
            }}
          >
            <Grid container spacing={2} alignItems="center">
              {/* Submenu Name Input */}
              <Grid item xs={12} sm={6} md={3}>
                <TextField
                  label="Submenu Name"
                  variant="outlined"
                  size="small"
                  fullWidth
                  sx={{ backgroundColor: "#fff", borderRadius: 1 }}
                  value={submenu.name}
                  onChange={(e) =>
                    handleInputChange(index, "name", e.target.value)
                  }
                />
              </Grid>

              {/* URL Input */}
              <Grid item xs={12} sm={6} md={3}>
                <TextField
                  label="Routes"
                  variant="outlined"
                  size="small"
                  fullWidth
                  sx={{ backgroundColor: "#fff", borderRadius: 1 }}
                  value={submenu.routes}
                  onChange={(e) =>
                    handleInputChange(index, "routes", e.target.value)
                  }
                />
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <TextField
                  label="Icon"
                  variant="outlined"
                  size="small"
                  fullWidth
                  sx={{ backgroundColor: "#fff", borderRadius: 1 }}
                  value={submenu.icon}
                  onChange={(e) =>
                    handleInputChange(index, "icon", e.target.value)
                  }
                />
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <Autocomplete
                  value={
                    permissionsList
                      .flatMap((item) => item.permissions)
                      .find(
                        (permission) =>
                          permission.id === subMenus[index].permission
                      ) || null // Match the current permission ID to the permission object
                  }
                  options={permissionsList.flatMap((item) =>
                    item.permissions.map((permission) => ({
                      category: item.PageAccessControl,
                      name: permission.name,
                      id: permission.id,
                    }))
                  )}
                  groupBy={(option) => option.category}
                  getOptionLabel={(option) => option.name || ""}
                  onChange={(event, newValue) => {
                    const updatedSubMenus = [...subMenus];
                    updatedSubMenus[index].permission = newValue
                      ? newValue.id
                      : null; // Save only the permission ID
                    setSubMenus(updatedSubMenus);
                  }}
                  renderOption={(props, option) => {
                    const isSelected = subMenus[index].permission === option.id; // Check if this option is selected
                    return (
                      <li
                        {...props}
                        style={{
                          backgroundColor: isSelected
                            ? "#e0f7fa"
                            : "transparent",
                          fontWeight: isSelected ? "bold" : "normal",
                        }}
                      >
                        <div style={{ marginLeft: "10px" }}>
                           {option.name}
                        </div>
                      </li>
                    );
                  }}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Select Permission"
                      variant="outlined"
                      size="small"
                      sx={{ backgroundColor: "#fff", borderRadius: "4px" }}
                    />
                  )}
                  ListboxProps={{
                    style: { maxHeight: 300, overflow: "auto" },
                    ref: (listbox) => {
                      if (listbox) {
                        // Scroll to the selected option
                        const selectedItem = listbox.querySelector(
                          `[data-option-index="${permissionsList
                            .flatMap((item) => item.permissions)
                            .findIndex(
                              (permission) =>
                                permission.id === subMenus[index].permission
                            )}"]`
                        );
                        if (selectedItem) {
                          selectedItem.scrollIntoView({ block: "nearest" });
                        }
                      }
                    },
                  }}
                />
              </Grid>

              {/* Active Checkbox */}
              <Grid item xs={6} sm={3} md={2}>
                <Box sx={{ display: "flex", alignItems: "center" }}>
                  <Typography variant="body2" sx={{ marginRight: 1 }}>
                    Active
                  </Typography>
                  <Checkbox
                    checked={submenu.is_active}
                    onChange={(e) =>
                      handleInputChange(index, "is_active", e.target.checked)
                    }
                  />
                </Box>
              </Grid>

              {/* Admin Checkbox */}
              <Grid item xs={6} sm={3} md={2}>
                <Box sx={{ display: "flex", alignItems: "center" }}>
                  <Typography variant="body2" sx={{ marginRight: 1 }}>
                    Admin
                  </Typography>
                  <Checkbox
                    checked={submenu.is_admin}
                    onChange={(e) =>
                      handleInputChange(index, "is_admin", e.target.checked)
                    }
                  />
                </Box>
              </Grid>
              {/* Customer Checkbox */}
              <Grid item xs={6} sm={3} md={2}>
                <Box sx={{ display: "flex", alignItems: "center" }}>
                  <Typography variant="body2" sx={{ marginRight: 1 }}>
                    Customer
                  </Typography>
                  <Checkbox
                    checked={submenu.is_customer}
                    onChange={(e) =>
                      handleInputChange(index, "is_customer", e.target.checked)
                    }
                  />
                </Box>
              </Grid>

              {/* Add or Delete Icon */}
              <Grid item xs={6} sm={3} md={2}>
                <Box sx={{ display: "flex", justifyContent: "center", gap: 1 }}>
                  {index === subMenus.length - 1 ? (
                    <>
                      <IconButton onClick={handleAddClick} color="primary">
                        <AddCircleOutlineIcon />
                      </IconButton>
                      {subMenus.length > 1 && (
                        <IconButton
                          onClick={() => handleDeleteClick(index)}
                          color="error"
                        >
                          <DeleteIcon />
                        </IconButton>
                      )}
                    </>
                  ) : (
                    <IconButton
                      onClick={() => handleDeleteClick(index)}
                      color="error"
                    >
                      <DeleteIcon />
                    </IconButton>
                  )}
                </Box>
              </Grid>
            </Grid>
          </Box>
        </motion.div>
      ))}
    </motion.div>
  );
};

export default SubMenuCard;
